import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../services/api';

export interface ProjectTask {
  _id: string;
  phase: string;
  project: string;
  createdBy: { _id: string; name: string; email: string };
  assignedTo: { _id: string; name: string; email: string };
  notes: string;
  media: { contentType: string }[];
  mediaCount: number;
  mediaUrls: string[];
  isComplete: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ProjectTasksState {
  tasks: { [phaseId: string]: ProjectTask[] };
  loading: { [phaseId: string]: boolean };
  error: string | null;
}

const initialState: ProjectTasksState = {
  tasks: {},
  loading: {},
  error: null,
};

export const fetchPhaseTasks = createAsyncThunk(
  'projectTasks/fetchPhaseTasks',
  async (phaseId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/project-tasks/${phaseId}`);
      return { phaseId, tasks: response.data as ProjectTask[] };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch tasks');
    }
  }
);

export const createPhaseTask = createAsyncThunk(
  'projectTasks/createPhaseTask',
  async (
    { phaseId, notes, files, assignedTo }: { phaseId: string; notes: string; files: File[]; assignedTo: string },
    { rejectWithValue }
  ) => {
    try {
      const formData = new FormData();
      formData.append('notes', notes);
      formData.append('assignedTo', assignedTo);
      files.forEach((file) => formData.append('media', file));

      const response = await api.post(`/project-tasks/${phaseId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { phaseId, task: response.data as ProjectTask };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create task');
    }
  }
);

export const editPhaseTask = createAsyncThunk(
  'projectTasks/editPhaseTask',
  async (
    { phaseId, taskId, notes, isComplete, assignedTo }: {
      phaseId: string;
      taskId: string;
      notes?: string;
      isComplete?: boolean;
      assignedTo?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const body: Record<string, any> = {};
      if (notes !== undefined) body.notes = notes;
      if (isComplete !== undefined) body.isComplete = isComplete;
      if (assignedTo !== undefined) body.assignedTo = assignedTo;

      const response = await api.put(`/project-tasks/${phaseId}/${taskId}`, body);
      return { phaseId, task: response.data as ProjectTask };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to edit task');
    }
  }
);

export const deletePhaseTask = createAsyncThunk(
  'projectTasks/deletePhaseTask',
  async ({ phaseId, taskId }: { phaseId: string; taskId: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/project-tasks/${phaseId}/${taskId}`);
      return { phaseId, taskId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete task');
    }
  }
);

const projectTasksSlice = createSlice({
  name: 'projectTasks',
  initialState,
  reducers: {
    clearTasks: (state) => {
      state.tasks = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPhaseTasks.pending, (state, action) => {
        state.loading[action.meta.arg] = true;
      })
      .addCase(fetchPhaseTasks.fulfilled, (state, action: PayloadAction<{ phaseId: string; tasks: ProjectTask[] }>) => {
        state.loading[action.payload.phaseId] = false;
        state.tasks[action.payload.phaseId] = action.payload.tasks;
      })
      .addCase(fetchPhaseTasks.rejected, (state, action) => {
        state.loading[action.meta.arg] = false;
        state.error = action.payload as string;
      })
      .addCase(createPhaseTask.fulfilled, (state, action: PayloadAction<{ phaseId: string; task: ProjectTask }>) => {
        const { phaseId, task } = action.payload;
        if (!state.tasks[phaseId]) state.tasks[phaseId] = [];
        state.tasks[phaseId].push(task);
      })
      .addCase(createPhaseTask.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(editPhaseTask.fulfilled, (state, action: PayloadAction<{ phaseId: string; task: ProjectTask }>) => {
        const { phaseId, task } = action.payload;
        const list = state.tasks[phaseId];
        if (list) {
          const index = list.findIndex((t) => t._id === task._id);
          if (index !== -1) list[index] = task;
        }
      })
      .addCase(deletePhaseTask.fulfilled, (state, action: PayloadAction<{ phaseId: string; taskId: string }>) => {
        const { phaseId, taskId } = action.payload;
        if (state.tasks[phaseId]) {
          state.tasks[phaseId] = state.tasks[phaseId].filter((t) => t._id !== taskId);
        }
      });
  },
});

export const { clearTasks } = projectTasksSlice.actions;
export default projectTasksSlice.reducer;
